/**
 * Imprimer un bon d'intervention (lot I2.2 puis « bon refondu », ADR-0014 §12 à §14) : UNE feuille A4 — logos et
 * coordonnées de l'entreprise en tête, deux emplacements de signature au pied — que l'impression du navigateur, ou
 * « Enregistrer en PDF », reproduit telle quelle. Aucune bibliothèque PDF n'est ajoutée : même principe que la gamme et
 * les planches d'étiquettes QR.
 *
 * La feuille suit le MODÈLE de la nature du bon (entretien, dépannage, installation, livraison, location) : ses rubriques, dans
 * l'ordre validé sur les ébauches du 2026-10-16, sa pastille « sous contrat / facturé » à côté du titre, ses deux signatures.
 * Une livraison faite sur un site seul n'a pas de bande de machine.
 *
 * Tout ce qui s'affiche vient de `construireDocumentBon` (domaine pur). Un bon « à valider » s'imprime avec la mention
 * « BON NON VALIDÉ » ; un brouillon et un bon annulé ne s'impriment pas. Le texte garde sa taille : un bon qui ne tient
 * pas sur une page passe sur une deuxième, coupée proprement.
 *
 * Le titre de la page est le nom du document (« BI-2026-00001 – KAESER-ESD442SFC-1312 – AIR01 ») : c'est le nom que le
 * navigateur propose pour le PDF. On y arrive depuis le détail d'un bon : `?bon=…`. Les logos se chargent AVANT que le
 * bouton d'impression ne s'active : un document imprimé trop tôt partirait sans eux.
 */
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, api, type BonDetail } from '../api/client';
import { paliersDeLaGamme } from '../domaine/bon-edition';
import {
  MENTION_NON_VALIDE,
  PRECISION_NON_VALIDE,
  construireDocumentBon,
  grilleDesValeurs,
  nomDocumentBon,
  phraseLongueur,
  rangeesDeValeurs,
  type CompteurConnu,
  type GrilleValeurs,
  type PalierConnu,
  type DocumentBon,
} from '../domaine/bon-document';
import { classeFacturation } from '../domaine/bon-modeles';
import { useIdentiteEspace } from '../domaine/identite-espace';
import { LIBELLE_ETAT, aujourdhuiLocal } from '../domaine/interventions';
import { CONSEIL_EN_TETES_NAVIGATEUR, installerPageBon } from '../domaine/page-impression';
import { useUrlsFichiers } from '../domaine/urls-fichiers';

export function BonImpressionPage() {
  const [params] = useSearchParams();
  const bonId = params.get('bon');
  const identite = useIdentiteEspace();

  const [bon, setBon] = useState<BonDetail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [compteurs, setCompteurs] = useState<readonly CompteurConnu[]>([]);
  const [paliers, setPaliers] = useState<readonly PalierConnu[]>([]);
  // Les libellés des compteurs et des paliers se lisent sur la machine : le document n'est construit qu'une fois
  // qu'ils sont arrivés (ou ont échoué), pour ne pas imprimer un repli qu'une réponse en route aurait remplacé.
  const [extrasPrets, setExtrasPrets] = useState(false);

  useEffect(() => {
    setBon(null);
    setErreur(null);
    setExtrasPrets(false);
    setCompteurs([]);
    setPaliers([]);
    if (!bonId) {
      setErreur('Aucun bon à imprimer : ouvrez cette page depuis un bon.');
      return;
    }
    let actif = true;
    api
      .bon(bonId)
      .then((b) => {
        if (actif) setBon(b);
      })
      .catch((e: unknown) => {
        if (actif) setErreur(e instanceof ApiError ? e.message : 'Bon illisible.');
      });
    return () => {
      actif = false;
    };
  }, [bonId]);

  const machineId = bon?.asset?.id ?? null;
  const bonCharge = bon !== null;
  useEffect(() => {
    if (!bonCharge) return;
    // Une livraison sur un site seul n'a ni compteur ni gamme à lire : le document peut se construire tout de suite.
    if (!machineId) {
      setExtrasPrets(true);
      return;
    }
    let actif = true;
    // Un compteur ou une gamme illisibles ne bloquent pas l'impression : le document dit « libellé indisponible ».
    const lectureCompteurs = api
      .counters(machineId)
      .then((liste) => {
        if (actif) {
          setCompteurs(liste.map((c) => ({ id: c.id, label: c.label, unit: c.unit, drivesMaintenance: c.drivesMaintenance })));
        }
      })
      .catch(() => undefined);
    const lectureGamme = api
      .assetMaintenance(machineId)
      .then((m) => {
        if (actif) setPaliers(paliersDeLaGamme(m).map((p) => ({ levelKey: p.levelKey, code: p.code, label: p.label })));
      })
      .catch(() => undefined);
    void Promise.all([lectureCompteurs, lectureGamme]).then(() => {
      if (actif) setExtrasPrets(true);
    });
    return () => {
      actif = false;
    };
  }, [bonCharge, machineId]);

  // Le document attend aussi l'identité de l'espace : sans elle, l'en-tête partirait vide pendant que la réponse est en route.
  const resultat = useMemo(() => {
    if (!bon || !extrasPrets || !identite) return null;
    return construireDocumentBon({
      bon,
      compteurs,
      paliers,
      identite: {
        nom: identite.nom,
        adresse: identite.adresse,
        telephone: identite.telephone,
        email: identite.email,
        siteWeb: identite.siteWeb,
      },
      edition: aujourdhuiLocal(),
    });
  }, [bon, extrasPrets, compteurs, paliers, identite]);

  const doc = resultat?.ok ? resultat.document : null;

  // Les logos : leurs adresses, puis leur chargement réel dans la page. Un logo dont l'adresse ou l'image échoue est écarté
  // (le bon reste imprimable sans lui), mais on attend sa réponse avant d'activer le bouton.
  // Les signatures de la clôture (lot T3) se chargent avec les logos : le bouton attend leurs images aussi.
  const imageClientId = doc?.signature.cloture?.imageClientId ?? null;
  const imageTechnicienId = doc?.signature.cloture?.imageTechnicienId ?? null;
  const idsLogos = useMemo(
    () => [
      ...(identite?.logoPrincipalId ? [identite.logoPrincipalId] : []),
      ...(identite?.logosSecondairesIds ?? []),
      ...(imageClientId ? [imageClientId] : []),
      ...(imageTechnicienId ? [imageTechnicienId] : []),
    ],
    [identite, imageClientId, imageTechnicienId],
  );
  const { urls: urlsLogos, pret: urlsPretes } = useUrlsFichiers(idsLogos);
  const [logosTermines, setLogosTermines] = useState<Readonly<Record<string, 'ok' | 'ko'>>>({});
  const logoTermine = (id: string, etat: 'ok' | 'ko') => setLogosTermines((t) => (t[id] === etat ? t : { ...t, [id]: etat }));
  const logosPrets = urlsPretes && idsLogos.every((id) => !urlsLogos[id] || logosTermines[id] !== undefined);
  const peutImprimer = doc !== null && logosPrets;

  // Le titre de la page devient le nom du document : c'est le nom que Chrome et Edge proposent au PDF.
  const nom = doc ? nomDocumentBon(doc) : null;
  useEffect(() => {
    if (!nom) return;
    const avant = window.document.title;
    window.document.title = nom;
    return () => {
      window.document.title = avant;
    };
  }, [nom]);

  // Marges de la feuille : une règle @page ordinaire, ajoutée tant que cette page est affichée (voir
  // domaine/page-impression.ts : aucune page nommée, Edge les perdait à partir de la 3e feuille).
  const valide = doc?.valide ?? true;
  useEffect(() => installerPageBon(window.document, valide), [valide]);

  const longueur = doc ? phraseLongueur(doc) : null;

  return (
    <>
      {/* Tout ce bloc disparaît à l'impression : seule la feuille sort. */}
      <div className="barre-impression">
        <div className="fil-ariane">
          <Link to={bonId ? `/interventions?bon=${bonId}` : '/interventions'}>← Retour au bon</Link>
        </div>

        <div className="entete-page">
          <div>
            <h1>Imprimer le bon</h1>
            <p>
              {bon
                ? `${bon.number} — ${bon.nature.label} (${LIBELLE_ETAT[bon.status].toLowerCase()})`
                : erreur
                  ? 'Bon indisponible.'
                  : 'Préparation du document…'}
            </p>
          </div>
          <div className="actions">
            <button disabled={!peutImprimer} onClick={() => window.print()}>
              {doc && !logosPrets ? 'Chargement des logos…' : 'Imprimer / Enregistrer en PDF'}
            </button>
          </div>
        </div>

        {erreur && <div className="message erreur">{erreur}</div>}
        {resultat && !resultat.ok && <div className="message avertissement">{resultat.raison}</div>}
        {doc && !doc.valide && (
          <div className="message info">
            Ce bon n’est pas encore validé : le papier porte la mention « {MENTION_NON_VALIDE} ». Une fois validé, il pourra être
            imprimé sans elle.
          </div>
        )}
        {longueur && <div className="message info">{longueur}</div>}
        <div className="message info">{CONSEIL_EN_TETES_NAVIGATEUR}</div>
      </div>

      {doc && (
        <FeuilleBon
          doc={doc}
          logoPrincipal={identite?.logoPrincipalId ? (urlsLogos[identite.logoPrincipalId] ?? null) : null}
          logosSecondaires={(identite?.logosSecondairesIds ?? []).flatMap((id) => {
            const url = urlsLogos[id];
            return url ? [{ id, url }] : [];
          })}
          idPrincipal={identite?.logoPrincipalId ?? null}
          onLogo={logoTermine}
          signatures={{
            client: imageClientId ? { id: imageClientId, url: urlsLogos[imageClientId] ?? null } : null,
            technicien: imageTechnicienId ? { id: imageTechnicienId, url: urlsLogos[imageTechnicienId] ?? null } : null,
          }}
        />
      )}
    </>
  );
}

function Champ({ libelle, fort, children }: { libelle: string; fort?: boolean; children: ReactNode }) {
  return (
    <div className="bon-champ">
      <b>{libelle}</b>
      <span className={fort ? 'fort' : undefined}>{children}</span>
    </div>
  );
}

/**
 * Les valeurs relevées : « Heures de fonctionnement 66 665 h », la valeur en gras collée à son libellé. Toutes les lignes
 * partagent les mêmes colonnes (elles s'alignent d'une ligne à l'autre) ; une ligne plus courte s'étale quand le compte tombe juste.
 */
function GrilleDesValeurs({ grille }: { grille: GrilleValeurs }) {
  return (
    <>
      {grille.lignes.map((ligne, i) => (
        <div key={i} className="bon-ligne-valeurs" style={{ gridTemplateColumns: `repeat(${grille.colonnes}, 1fr)` }}>
          {ligne.map(({ releve: r, span }, j) => (
            <span key={j} style={{ gridColumn: `span ${span}` }}>
              {r.libelle}
              <b className={r.vide ? 'bon-vide' : undefined}>
                {r.valeur}
                {r.unite && ` ${r.unite}`}
              </b>
            </span>
          ))}
        </div>
      ))}
    </>
  );
}

/** Un cadre de texte libre (problème signalé, travaux réalisés, réserves du client, observations) : vide, il reste à remplir à la main. */
function CadreTexte({ titre, texte, haut, classe }: { titre: string; texte: string | null; haut?: boolean; classe?: string }) {
  return (
    <section className={`bon-rubrique insecable${classe ? ` ${classe}` : ''}`}>
      <h2>{titre}</h2>
      <div className={`bon-cadre bon-observations${haut ? ' bon-cadre-haut' : ''}`}>{texte}</div>
    </section>
  );
}

function FeuilleBon({
  doc,
  logoPrincipal,
  logosSecondaires,
  idPrincipal,
  onLogo,
  signatures,
}: {
  doc: DocumentBon;
  signatures: {
    readonly client: { readonly id: string; readonly url: string | null } | null;
    readonly technicien: { readonly id: string; readonly url: string | null } | null;
  };
  logoPrincipal: string | null;
  logosSecondaires: readonly { readonly id: string; readonly url: string }[];
  idPrincipal: string | null;
  onLogo: (id: string, etat: 'ok' | 'ko') => void;
}) {
  const cloture = doc.signature.cloture;
  /** Une signature dessinée (lot T3) : son image, ou un cadre vide quand elle n’a pas pu être lue. */
  const image = (s: { readonly id: string; readonly url: string | null } | null, alt: string): ReactNode =>
    s?.url ? (
      <img className="bon-signature-image" src={s.url} alt={alt} onLoad={() => onLogo(s.id, 'ok')} onError={() => onLogo(s.id, 'ko')} />
    ) : null;
  const aReleves = doc.relevesHeures.length > 0 || doc.relevesAutres.length > 0;
  const { colonnes } = doc;

  /** Les rubriques de la feuille, dans l'ordre du modèle (`doc.ordre`) : seules celles qui ont de quoi s'imprimer sortent. */
  const rubriques: Record<string, ReactNode> = {
    probleme: doc.probleme !== null && <CadreTexte titre="Problème signalé" texte={doc.probleme} haut />,
    travaux: doc.travaux !== null && <CadreTexte titre="Travaux réalisés" texte={doc.travaux} haut />,
    controles: doc.controles.length > 0 && (
      <section className="bon-rubrique insecable">
        <h2>{doc.titreControles}</h2>
        <div className="bon-cases" style={{ ['--lignes' as string]: doc.controlesLignes }}>
          {doc.controles.map((c, i) => (
            <div key={i} className="bon-case">
              <span>{c.libelle}</span>
              <span className={`bon-boite${c.fait ? ' ok' : ''}`} role="img" aria-label={c.fait ? 'Fait' : 'Non fait'} />
            </div>
          ))}
        </div>
      </section>
    ),
    valeurs: aReleves && (
      <section className="bon-rubrique insecable">
        <h2>{doc.titreValeurs}</h2>
        <GrilleDesValeurs grille={grilleDesValeurs(rangeesDeValeurs(doc))} />
      </section>
    ),
    realise: doc.entretien && (
      <section className="bon-rubrique insecable">
        <div className="bon-realise">
          <b>Entretien réalisé :</b> {doc.entretien.code ? `palier ${doc.entretien.code}` : 'un palier de la gamme'}
          {doc.entretien.libelle && ` (${doc.entretien.libelle})`}
          {doc.entretien.heures && ` à ${doc.entretien.heures} h`}
        </div>
      </section>
    ),
    miseEnService: doc.miseEnService !== null && (
      <section className="bon-rubrique insecable">
        <div className="bon-realise">
          <b>Mise en service effectuée le</b> {doc.miseEnService}
        </div>
      </section>
    ),
    dossier: doc.dossier && (
      <section className="bon-rubrique insecable">
        <div className="bon-realise">
          <b>Dossier technique et certificats :</b> remis en main propre
          <span className={`bon-boite${doc.dossier.remis ? ' ok' : ''}`} role="img" aria-label={doc.dossier.remis ? 'Coché' : 'Non coché'} />
          laissé dans l’armoire électrique
          <span className={`bon-boite${doc.dossier.armoire ? ' ok' : ''}`} role="img" aria-label={doc.dossier.armoire ? 'Coché' : 'Non coché'} />
        </div>
      </section>
    ),
    periode: doc.periode && (
      <section className="bon-rubrique insecable">
        <div className="bon-realise">
          <b>Mise à disposition le</b> {doc.periode.du} &nbsp;—&nbsp; <b>retour prévu le</b> {doc.periode.au}
        </div>
      </section>
    ),
    lignes: doc.articles.length > 0 && (
      <section className="bon-rubrique">
        <h2>{doc.titreLignes}</h2>
        <table>
          <thead>
            <tr>
              {colonnes.type && <th className="col-type">Type</th>}
              {colonnes.reference && <th className="col-ref">Référence</th>}
              <th>Désignation</th>
              {colonnes.serie === 'COLONNE' && <th className="col-serie">N° de série</th>}
              <th className="col-qte">Quantité</th>
            </tr>
          </thead>
          <tbody>
            {doc.articles.map((a, i) => (
              <tr key={i}>
                {colonnes.type && <td>{a.type}</td>}
                {colonnes.reference && <td className="col-ref">{a.reference ?? ''}</td>}
                <td>
                  {a.designation}
                  {colonnes.serie === 'DANS_DESIGNATION' && a.serie && ` — n° de série ${a.serie}`}
                  {a.note && <div className="bon-note-ligne">{a.note}</div>}
                </td>
                {colonnes.serie === 'COLONNE' && <td className="col-serie">{a.serie ?? '—'}</td>}
                <td className="col-qte">{a.quantite}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    ),
    reserves: doc.reserves !== null && <CadreTexte titre="Réserves du client" texte={doc.reserves} />,
    suites: (
      <section className="bon-rubrique insecable">
        <h2>Suites à donner</h2>
        <div className="bon-cadre bon-suites">
          {doc.suites.map((s, i) => (
            <div key={i}>
              <b>{s.type}</b> — {s.commentaire}
            </div>
          ))}
        </div>
      </section>
    ),
    observations: <CadreTexte titre="Observations" texte={doc.observations} classe="avant-signatures" />,
  };

  return (
    <article className="bon-feuille">
      <header className="bon-haut">
        <div className="bon-logo-principal">
          {logoPrincipal && idPrincipal && (
            <img
              src={logoPrincipal}
              alt={doc.entete.nom ?? 'Logo de l’entreprise'}
              onLoad={() => onLogo(idPrincipal, 'ok')}
              onError={() => onLogo(idPrincipal, 'ko')}
            />
          )}
        </div>
        <div className="bon-coordonnees">
          {doc.entete.nom && <b>{doc.entete.nom}</b>}
          {doc.entete.lignes.map((ligne, i) => (
            <div key={i}>{ligne}</div>
          ))}
        </div>
        <div className="bon-logos-secondaires">
          {logosSecondaires.map((l, i) => (
            <img key={l.id} src={l.url} alt={`Partenaire ${i + 1}`} onLoad={() => onLogo(l.id, 'ok')} onError={() => onLogo(l.id, 'ko')} />
          ))}
        </div>
      </header>

      <div className="bon-titre">
        <span className="bon-titre-gauche">
          <span className="bon-nature">
            {doc.nature}
            {doc.numeroPraxedo && <span className="bon-praxedo"> — n° Praxedo {doc.numeroPraxedo}</span>}
          </span>
          {doc.facturation && (
            <span className={`pastille-facturation ${classeFacturation(doc.facturation.code)}`}>{doc.facturation.libelle}</span>
          )}
        </span>
        {!doc.valide && (
          <div className="bon-mention" role="note">
            {MENTION_NON_VALIDE}
            <span>{PRECISION_NON_VALIDE}</span>
          </div>
        )}
        <div className="bon-cartouche">
          <small>N° D’INTERVENTION</small>
          <b>{doc.numero}</b>
        </div>
      </div>

      {doc.machine && (
        <div className="bon-machine">
          <span className="bon-machine-nom">{doc.machine.designation}</span>
          {doc.machine.designationClient && <span className="bon-pastille">{doc.machine.designationClient}</span>}
        </div>
      )}

      <section className="bon-bloc">
        <div>
          {doc.client && (
            <Champ libelle="Entreprise" fort>
              {doc.client}
            </Champ>
          )}
          {doc.site && <Champ libelle="Site">{doc.site.nom}</Champ>}
          {doc.site?.adresse && <Champ libelle="Adresse">{doc.site.adresse}</Champ>}
          {doc.contact && <Champ libelle="Contact">{doc.contact}</Champ>}
        </div>
        <div>
          <Champ libelle="Date">{doc.date}</Champ>
          {doc.techniciens.length > 0 && (
            <Champ libelle={doc.libelleTechniciens}>
              {doc.techniciens.map((nom, i) => (
                <span key={i} className="bon-technicien">
                  {nom}
                </span>
              ))}
            </Champ>
          )}
        </div>
      </section>

      {doc.ordre.map((r) => (
        <Fragment key={r}>{rubriques[r] ?? null}</Fragment>
      ))}

      {/* Les signatures et le pied ne se séparent JAMAIS : le pied seul sur une page 2 vide (vu sur un PDF réel à 20 articles). */}
      <div className="bon-fin">
        <section className="bon-signatures" aria-label="Signatures">
          <div className="bon-signature">
            <b className="bon-signature-titre">{doc.signature.client}</b>
            {cloture?.absent ? (
              <span className="bon-signature-absent">Client absent : {cloture.absent}</span>
            ) : cloture ? (
              <>
                {image(signatures.client, 'Signature du client')}
                <span className="bon-signature-legende">
                  {cloture.nomClient}
                  {cloture.dateClient ? ` — le ${cloture.dateClient}` : ''}
                </span>
              </>
            ) : (
              <>
                <span>Nom :</span>
                <span>Date :</span>
                <span>Signature :</span>
              </>
            )}
          </div>
          <div className="bon-signature">
            <b className="bon-signature-titre">{doc.signature.technicien}</b>
            {doc.signature.techniciens && <span className="bon-signature-noms">{doc.signature.techniciens}</span>}
            <span>Date : {doc.signature.date ?? ''}</span>
            {cloture ? image(signatures.technicien, 'Signature du technicien') : <span>Signature :</span>}
          </div>
        </section>

        <footer className="bon-pied">Édité le {doc.edition}</footer>
      </div>
    </article>
  );
}
